import { z } from 'zod';

// Números do Postgres (NUMERIC / count) chegam como string — coerção defensiva.
const num = z.coerce.number();
const numNull = z.coerce.number().nullable();

export const talhaoResumoSchema = z.object({
  talhao_id: num,
  imovel_id: numNull,
  fazenda: z.string().nullable(),
  proprietario_cpf: z.string().nullable(),
  produtor: z.string().nullable(),
  numero: z.string(),
  talhao_nome: z.string().nullable(),
  area_ha: numNull,
  safra_atual: z.string().nullable(),
  cultura_atual: z.string().nullable(),
  variedade_atual: z.string().nullable(),
  qtd_visitas: num,
  ultima_visita: z.string().nullable(),
  // Migration 081 — município/UF do imóvel pro card de fazenda. Optional
  // pra tolerar back ainda sem a view nova durante deploy.
  municipio: z.string().nullable().optional(),
  uf: z.string().nullable().optional(),
});
export type TalhaoResumo = z.infer<typeof talhaoResumoSchema>;

export const talhaoResumoListSchema = z.array(talhaoResumoSchema);

// Hierarquia Produtor → Fazenda (fluxo novo do app)
export const produtorSchema = z.object({
  cpf: z.string(),
  nome: z.string(),
  fazendas: num,
  total_ha: numNull,
  talhoes: num,
  ultima_visita: z.string().nullable(),
});
export type Produtor = z.infer<typeof produtorSchema>;
export const produtorListSchema = z.array(produtorSchema);

export const fazendaResumoSchema = z.object({
  imovel_id: num,
  fazenda: z.string().nullable(),
  municipio: z.string().nullable(),
  uf: z.string().nullable(),
  area_ha: numNull,
  talhoes: num,
  ultima_visita: z.string().nullable(),
});
export type FazendaResumo = z.infer<typeof fazendaResumoSchema>;
export const fazendaResumoListSchema = z.array(fazendaResumoSchema);

export const talhaoDetailSchema = talhaoResumoSchema.extend({
  geojson: z.unknown().nullable().optional(),
  lat: numNull.optional(),
  lon: numNull.optional(),
});
export type TalhaoDetail = z.infer<typeof talhaoDetailSchema>;

// ── Edição de fazenda (GET /agronomia/fazendas/:id) ──────────────────────────
export const fazendaEditTalhaoSchema = z.object({
  id: num,
  numero: z.string(),
  nome: z.string().nullable(),
  area_ha: numNull,
  tem_visitas: z.boolean(),
});
export type FazendaEditTalhao = z.infer<typeof fazendaEditTalhaoSchema>;

export const fazendaEditSchema = z.object({
  id: num,
  nome: z.string().nullable(),
  municipio: z.string().nullable(),
  uf: z.string().nullable(),
  arrendada: z.boolean(),
  agro_produtor_cpf: z.string().nullable().optional(),
  talhoes: z.array(fazendaEditTalhaoSchema),
});
export type FazendaEdit = z.infer<typeof fazendaEditSchema>;

export const relatorioListItemSchema = z.object({
  id: num,
  data_visita: z.string(),
  agronomo_nome: z.string().nullable(),
  agronomo_telefone: z.string().nullable(),
  comentario: z.string().nullable(),
  qtd_pragas: num,
  qtd_doencas: num,
  qtd_daninhas: num,
  qtd_nutricao: num,
  qtd_alertas: num,
  metodo_localizacao: z.string().nullable(),
  origem: z.string().nullable(),
  status: z.string().nullable(),
  // 089 — visita de app: deep-link pro relatório de sessão (vários talhões).
  visita_campo_id: numNull.optional(),
  estadio_fenologico: z.string().nullable().optional(),
  fotos: num,
});
export type RelatorioListItem = z.infer<typeof relatorioListItemSchema>;

export const relatorioListSchema = z.array(relatorioListItemSchema);

export const ocorrenciaSchema = z.object({
  tipo: z.string(),
  nome: z.string().nullable(),
  severidade: z.string().nullable(),
  quantidade: numNull,
  unidade: z.string().nullable(),
  observacao: z.string().nullable(),
});

export const fotoSchema = z.object({
  id: num,
  arquivo: z.string(),
  legenda: z.string().nullable(),
  lat: numNull,
  lon: numNull,
  capturado_em: z.string().nullable(),
  recomendacao: z.string().nullable().optional(),
  estadio_fenologico: z.string().nullable().optional(),
});

export const relatorioDetailSchema = relatorioListItemSchema
  .omit({ fotos: true })
  .extend({
    talhao_id: num,
    ocorrencias: z.array(ocorrenciaSchema),
    fotos: z.array(fotoSchema),
  });
export type RelatorioDetail = z.infer<typeof relatorioDetailSchema>;

// ── Solo (ISRIC SoilGrids, base local) ──────────────────────────────────
export const soloDepthSchema = z.object({
  depth: z.string(),
  mean: numNull,
  n: num,
});
export const soloLayerSchema = z.object({
  prop: z.string(),
  label: z.string(),
  unit: z.string(),
  depths: z.array(soloDepthSchema),
});
export const soloSchema = z.object({
  talhao_id: num,
  pontos: num,
  cobertura: z.boolean(),
  classe_textural: z.string().nullable(),
  layers: z.array(soloLayerSchema),
});
export type Solo = z.infer<typeof soloSchema>;
export type SoloLayer = z.infer<typeof soloLayerSchema>;

// ── Clima (Open-Meteo) ───────────────────────────────────────────────────────
export const climaSchema = z.object({
  lat: num,
  lon: num,
  atual: z
    .object({
      temp: numNull,
      umidade: numNull,
      code: numNull,
      vento: numNull,
      precip: numNull,
    })
    .nullable(),
  dias: z.array(
    z.object({
      date: z.string(),
      code: numNull,
      tmax: numNull,
      tmin: numNull,
      precip: numNull,
      precip_prob: numNull,
    }),
  ),
});
export type Clima = z.infer<typeof climaSchema>;

// ── Manejo (telas 06-10: aplicações por categoria) ───────────────────────────
export const manejoAplicacaoSchema = z.object({
  id: num,
  data_aplicacao: z.string(),
  titulo: z.string().nullable(),
  detalhe: z.string().nullable(),
});
export const manejoAplicacaoListSchema = z.array(manejoAplicacaoSchema);
export type ManejoAplicacao = z.infer<typeof manejoAplicacaoSchema>;

// ── Visitas da fazenda para revisão (sincronizadas) ──────────────────────────
export const visitaRevisarSchema = z.object({
  id: num,
  data: z.string().nullable(),
  agronomo: z.string(),
  revisado: z.boolean().default(false),
  tem_resumo: z.boolean(),
  fotos: num,
  revisadas: num,
});
export const visitaRevisarListSchema = z.array(visitaRevisarSchema);
export type VisitaRevisar = z.infer<typeof visitaRevisarSchema>;

// ── Datas com visita (calendário do relatório consolidado, tela 12) ───────────
export const dataVisitaSchema = z.object({ data: z.string(), visitas: num });
export const datasVisitaListSchema = z.array(dataVisitaSchema);
export type DataVisita = z.infer<typeof dataVisitaSchema>;

// ── Catálogo de insumos (migration 099) ──────────────────────────────────────
export const insumoAtivoSchema = z.object({
  nome: z.string(),
  concentracao: numNull,
  concentracao_unidade: z.string().nullable(),
  grupo_quimico: z.string().nullable(),
  codigo_resistencia: z.string().nullable(),
  ph: numNull,
  kow: numNull,
});
export const insumoCulturaSchema = z.object({
  cultura: z.string(),
  dose_min: numNull,
  dose_max: numNull,
  dose_unidade: z.string().nullable(),
  carencia_dias: numNull,
  max_aplicacoes: numNull,
  intervalo_dias: numNull,
});
export const insumoSchema = z.object({
  id: num,
  nome_comercial: z.string(),
  categoria: z.string(),
  fabricante: z.string().nullable(),
  formulacao: z.string().nullable(),
  unidade: z.string().nullable(),
  modo_acao: z.string().nullable(),
  culturas: z.array(z.string()),
  reentrada_horas: numNull,
  classe_tox: z.string().nullable(),
  classe_ambiental: z.string().nullable(),
  numero_registro: z.string().nullable().optional(),
  fonte_bula_url: z.string().nullable(),
  ativo: z.boolean(),
  ativos: z.array(insumoAtivoSchema),
  culturas_bula: z.array(insumoCulturaSchema),
});
export type Insumo = z.infer<typeof insumoSchema>;
export type InsumoCultura = z.infer<typeof insumoCulturaSchema>;
export const insumoListSchema = z.array(insumoSchema);
